/**
 * Borrow — the in-app loan application flow (master plan §2.2).
 *
 * One continuous flow: amount (snapped slider) + term (discrete chips) with a
 * live server quote (the binding price), one honest confirm dialog showing
 * the total cost in Kwacha, then the visible status pipeline.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Clock, Loader2, ShieldAlert, Wallet, XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { useCustomer } from "@/contexts/CustomerContext";
import { formatCurrency } from "@/lib/lendingCalculations";
import { platformApi } from "@/platform/api";

const TERM_OPTIONS = [3, 6, 9, 12, 18, 24];
const AMOUNT_STEP = 500;
const PURPOSES = [
  "Business stock", "School fees", "Medical", "Home improvement",
  "Farming inputs", "Emergency", "Other",
];

type Quote = Awaited<ReturnType<typeof platformApi.quote>>;

export default function BorrowPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    kycApproved, kycStatus, planRules, activeLoan, loanApplications,
    pendingApplications, fetchData, isLoading,
  } = useCustomer();

  const minAmount = planRules.minAmount;
  const maxAmount = planRules.maxAmount;
  const maxTerm = planRules.maxTermMonths;
  const terms = TERM_OPTIONS.filter((t) => t <= maxTerm);

  const [amount, setAmount] = useState(minAmount);
  const [term, setTerm] = useState(terms.includes(6) ? 6 : terms[0]);
  const [purpose, setPurpose] = useState("");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Live server quote — debounced; the server price is the binding one.
  useEffect(() => {
    if (!kycApproved || activeLoan || pendingApplications > 0) return;
    setQuoteBusy(true);
    const handle = setTimeout(async () => {
      try {
        setQuote(await platformApi.quote(amount, term));
      } catch {
        setQuote(null);
      } finally {
        setQuoteBusy(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [amount, term, kycApproved, activeLoan, pendingApplications]);

  const pendingApplication = useMemo(
    () => loanApplications.find((a) => ["pending", "under_review"].includes(a.status)),
    [loanApplications],
  );
  const lastDecided = useMemo(
    () => loanApplications.find((a) => ["approved", "rejected"].includes(a.status)),
    [loanApplications],
  );

  const submit = async () => {
    setSubmitting(true);
    try {
      await platformApi.apply({ amount, term_months: term, purpose: purpose || undefined });
      setConfirmOpen(false);
      toast({ title: "Application submitted", description: "We'll notify you by SMS at every step." });
      await fetchData();
    } catch (error) {
      toast({
        title: "Could not submit",
        description: error instanceof Error ? error.message : "Try again shortly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  // ---- Gate: KYC first (progressive — shown after they can see the limits) ----
  if (!kycApproved) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-foreground">Borrow</h1>
          <p className="text-sm text-muted-foreground">Complete identity verification to unlock borrowing.</p>
        </header>
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <ShieldAlert className="h-8 w-8 text-warning" />
            <div>
              <h2 className="font-semibold text-foreground">
                {kycStatus === "pending" ? "Verification in review" : "One quick step first"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {kycStatus === "pending"
                  ? "Your documents are being reviewed — usually under 24 hours. We'll SMS you."
                  : "Verify your NRC once (under 2 minutes) and you can apply immediately."}
              </p>
            </div>
            {kycStatus !== "pending" && (
              <Button onClick={() => navigate("/dashboard/kyc")}>Verify my identity</Button>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">What you unlock</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-4 text-center">
            <div><p className="text-xs text-muted-foreground">Borrow up to</p><p className="font-semibold">{formatCurrency(maxAmount)}</p></div>
            <div><p className="text-xs text-muted-foreground">Terms up to</p><p className="font-semibold">{maxTerm} months</p></div>
            <div><p className="text-xs text-muted-foreground">Interest from</p><p className="font-semibold">{planRules.interestRate}% p.a.</p></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Status pipeline for an open application ----
  if (pendingApplication) {
    const steps = ["Submitted", "Under review", "Approved", "Money sent"];
    const current = pendingApplication.status === "under_review" ? 1 : 0;
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-foreground">Your application</h1>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(pendingApplication.amount)} over {pendingApplication.term_months} months
          </p>
        </header>
        <Card>
          <CardContent className="py-6">
            <ol className="space-y-4">
              {steps.map((label, index) => (
                <li key={label} className="flex items-center gap-3">
                  {index <= current
                    ? <CheckCircle2 className="h-5 w-5 text-success" />
                    : <Clock className="h-5 w-5 text-muted-foreground/50" />}
                  <span className={index <= current ? "font-medium text-foreground" : "text-muted-foreground"}>
                    {label}
                  </span>
                  {index === current && (
                    <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">now</span>
                  )}
                </li>
              ))}
            </ol>
            <p className="mt-6 text-sm text-muted-foreground">
              Most decisions are made within 48 hours. Every step is mirrored to you by SMS —
              once approved, the money goes straight to your mobile money wallet.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Active loan: borrowing reopens after settlement ----
  if (activeLoan) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-foreground">Borrow</h1>
          <p className="text-sm text-muted-foreground">You have a loan in progress.</p>
        </header>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <Wallet className="h-8 w-8 text-primary" />
            <p className="text-sm text-muted-foreground">
              Settle your current loan ({formatCurrency(activeLoan.outstanding_balance)} outstanding)
              to apply for a new one. On-time payments grow your limit.
            </p>
            <Button onClick={() => navigate("/dashboard/loans")}>
              Go to my loan <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- The application flow ----
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Borrow</h1>
        <p className="text-sm text-muted-foreground">Pick an amount and term — the price updates live.</p>
      </header>

      {lastDecided?.status === "rejected" && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-center gap-3 py-4">
            <XCircle className="h-5 w-5 shrink-0 text-destructive" />
            <p className="text-sm text-foreground">
              Your last application wasn't approved
              {lastDecided.decision_reason ? `: ${lastDecided.decision_reason}` : ""}. You can apply again.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">How much do you need?</CardTitle>
          <CardDescription>
            {formatCurrency(minAmount)} – {formatCurrency(maxAmount)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="mb-3 text-center text-3xl font-bold tabular-nums text-foreground">
              {formatCurrency(amount)}
            </p>
            <Slider
              value={[amount]}
              min={minAmount}
              max={maxAmount}
              step={AMOUNT_STEP}
              onValueChange={([v]) => setAmount(v)}
              aria-label="Loan amount"
            />
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Over how many months?</p>
            <div className="flex flex-wrap gap-2">
              {terms.map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={term === t ? "default" : "outline"}
                  onClick={() => setTerm(t)}
                >
                  {t} mo
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">What is it for?</p>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger><SelectValue placeholder="Select a purpose (optional)" /></SelectTrigger>
              <SelectContent>
                {PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border bg-muted/40 p-4">
            {quote && !quoteBusy ? (
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Monthly payment</p>
                  <p className="font-semibold tabular-nums">{formatCurrency(quote.monthly_payment)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total to repay</p>
                  <p className="font-semibold tabular-nums">{formatCurrency(quote.total_repayment)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cost of the loan</p>
                  <p className="font-semibold tabular-nums">{formatCurrency(quote.total_cost)}</p>
                </div>
              </div>
            ) : (
              <div className="flex justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          <Button className="w-full" size="lg" disabled={!quote || quoteBusy} onClick={() => setConfirmOpen(true)}>
            Continue
          </Button>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm your application</DialogTitle>
            <DialogDescription>The exact cost, before you commit — no surprises later.</DialogDescription>
          </DialogHeader>
          {quote && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">You borrow</span><span className="font-medium tabular-nums">{formatCurrency(quote.amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Interest rate</span><span className="font-medium tabular-nums">{quote.interest_rate}% p.a.</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{term} monthly payments of</span><span className="font-medium tabular-nums">{formatCurrency(quote.monthly_payment)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">First payment due</span><span className="font-medium">{new Date(quote.first_due_date).toLocaleDateString()}</span></div>
              <div className="flex justify-between border-t pt-2"><span className="font-medium text-foreground">Total you repay</span><span className="font-semibold tabular-nums">{formatCurrency(quote.total_repayment)}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>Back</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
